import { differenceInCalendarDays } from 'date-fns';
import type { LeaveBlock } from '@/domain/types';

/**
 * Per-artist leave tally by type (Prompt K, K4). Counts leave DAYS taken per kind,
 * inclusive of both the start and end date (a single-day block counts as 1). Built
 * on the existing LeaveBlock types plus the new `annual` type - client owns the
 * final recognised-type list, so this stays driven off the union.
 */

export type LeaveType = LeaveBlock['type'];

export const LEAVE_TYPES: LeaveType[] = ['rdo', 'comp', 'flex-swap', 'time-off', 'annual'];

export const LEAVE_TYPE_LABEL: Record<LeaveType, string> = {
  rdo: 'RDO',
  comp: 'Comp',
  'flex-swap': 'Flex-swap',
  'time-off': 'Time-off',
  annual: 'Annual',
};

export interface LeaveTally {
  byType: Record<LeaveType, number>;
  total: number;
}

/** Inclusive day-count for one leave block: single day === 1. */
export function leaveBlockDays(block: LeaveBlock): number {
  return differenceInCalendarDays(new Date(block.endDate), new Date(block.startDate)) + 1;
}

/** Sum leave days per type across a set of blocks, with a running total. */
export function leaveTallyByType(blocks: readonly LeaveBlock[]): LeaveTally {
  const byType = Object.fromEntries(LEAVE_TYPES.map((t) => [t, 0])) as Record<LeaveType, number>;
  let total = 0;
  for (const b of blocks) {
    const days = Math.max(0, leaveBlockDays(b));
    byType[b.type] += days;
    total += days;
  }
  return { byType, total };
}

/**
 * Leave types that eat into the annual allowance. L7 (17 Jul): RDO is workload
 * balancing (a rostered day off over a 2-4 week window), and comp compensates a
 * double-day already worked - so NEITHER consumes the paid-leave allowance. Only
 * annual / time-off / flex-swap draw it down.
 */
export const ALLOWANCE_CONSUMING_TYPES: readonly LeaveType[] = ['annual', 'time-off', 'flex-swap'];

/** Total allowance-consuming leave days (excludes RDO + comp) across the blocks. */
export function allowanceConsumingDays(blocks: readonly LeaveBlock[]): number {
  return blocks
    .filter((b) => ALLOWANCE_CONSUMING_TYPES.includes(b.type))
    .reduce((sum, b) => sum + Math.max(0, leaveBlockDays(b)), 0);
}
